import { Component, ChangeDetectionStrategy, EventEmitter, Output, Input, OnInit } from '@angular/core';
import { ConfirmationService } from '../../../services/confirmation.service';
import { T } from 'src/assets/i18n/translation-keys';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeUtil } from '../../../utilities/employee.utilities';
import { Employee } from '../../../models/employee';
import { AuthenticationService } from '../../../services/authentication.service';
import { BulkAction, BulkActionEvent } from '../../../models/interfaces/bulk-action.interface';
import { BulkActionPosition } from '../../../enums/bulk-action-position.enum';

@Component({
    selector: 'app-bulk-actions',
    templateUrl: './bulk-actions.component.html',
    styleUrls: ['./bulk-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BulkActionsComponent implements OnInit {
  public readonly T = T;
  public selectAll: boolean = false;
  public isAdmin: boolean = false;

  @Input() showArchive: boolean = false;
  @Input() archiveActionConfirmText: string;
  @Input() archiveActionText: string;
  @Input() showOnlyArchiveAction: boolean = false;
  @Input() showArchiveAction: boolean = true;
  @Input() hideSelectAllBtn: boolean = false;
  @Input() hasSelectedItems: boolean = false;
  @Input() customActions: BulkAction[] = [];

  @Output() archiveActionTriggered = new EventEmitter<void>();
  @Output() selectAllToggled = new EventEmitter<boolean>();
  @Output() bulkModeToggled = new EventEmitter<boolean>();
  @Output() customActionTriggered = new EventEmitter<BulkActionEvent>();

  private employee: Employee;

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly translateService: TranslateService,
    private readonly authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.employee = this.authenticationService.getCurrentEmployee();
    this.isAdmin = EmployeeUtil.IsAdmin(this.employee);

    if (!this.archiveActionConfirmText) {
      this.archiveActionConfirmText = this.translateService.instant(T.common.are_you_sure_archive_confirm_text);
    }
  }

  public onToggleSelect(bulkModeActive: boolean): void {
    this.selectAll = bulkModeActive;
    this.showArchive = bulkModeActive;
    this.bulkModeToggled.emit(bulkModeActive);
  }

  public toggleSelectAll(): void {
    this.selectAll = !this.selectAll;
    this.selectAllToggled.emit(this.selectAll);
  }

  public onBulkArchive(): void {
    if (!this.hasSelectedItems) {
      return;
    }

    this.confirmationService.confirmThis(
      this.archiveActionConfirmText,
      () => {
        this.archiveActionTriggered.emit();
      },
      () => {}
    );
  }

  public onCustomAction(action: BulkAction): void {
    if (!this.hasSelectedItems || action.disabled) {
      return;
    }

    const executeAction = () => {
      this.customActionTriggered.emit({
        actionId: action.id,
        selectedItems: []
      });
    };

    if (action.requiresConfirmation) {
      const confirmText = action.confirmModalText || this.translateService.instant(T.common.are_you_sure);
      this.confirmationService.confirmThis(
        confirmText,
        executeAction,
        () => {}
      );
    } else {
      executeAction();
    }
  }

  public getLeftActions(): BulkAction[] {
    return this.customActions.filter(action => action.position === BulkActionPosition.Left || !action.position);
  }

  public getRightActions(): BulkAction[] {
    return this.customActions.filter(action => action.position === BulkActionPosition.Right);
  }
}
